import { apiClient } from './client';
import type { Service, ServiceImpact, ServiceEnvironment, ServiceEnvironmentWrite, TierMapService } from '@/types/oscar';

/**
 * Shared CMDB service catalogue (GET /api/services) — core.vService.
 * The full list, including infrastructure/internal services, so an asset can
 * be tagged with whatever it supports.
 */
export function getServices(signal?: AbortSignal): Promise<Service[]> {
  // /api/cmdb/services — /api/services is taken by the access-reviews BusinessService endpoint.
  return apiClient.get<Service[]>('/api/cmdb/services', signal);
}

/** Cross-tenant impact for one service: supporting assets + CAB changes + projects. */
export function getServiceImpact(id: number, signal?: AbortSignal): Promise<ServiceImpact> {
  return apiClient.get<ServiceImpact>(`/api/cmdb/services/${id}/impact`, signal);
}

/** Set (0..3) or clear (null) the service's CMDB criticality tier. Returns the refreshed service. */
export function setServiceTier(id: number, tier: number | null): Promise<Service> {
  return apiClient.post<Service>(`/api/cmdb/services/${id}/tier`, { Tier: tier });
}

/** All services with their tier + supporting assets, for the tier / dependency map. */
export function getTierMap(signal?: AbortSignal): Promise<TierMapService[]> {
  return apiClient.get<TierMapService[]>('/api/cmdb/tier-map', signal);
}

interface ServiceAssetLink { ServiceId: number; AssetId: number }
/** Link an asset to a service from the Services page (writes oscar.AssetService),
 *  optionally recording which environment of the service the asset is in. */
export function linkServiceAsset(serviceId: number, assetId: number, environmentId?: number | null): Promise<ServiceAssetLink> {
  return apiClient.post<ServiceAssetLink>(`/api/cmdb/services/${serviceId}/assets`, { AssetId: assetId, EnvironmentId: environmentId ?? null });
}
/** Unlink an asset from a service (soft-delete the link). */
export function unlinkServiceAsset(serviceId: number, assetId: number): Promise<ServiceAssetLink> {
  return apiClient.delete<ServiceAssetLink>(`/api/cmdb/services/${serviceId}/assets/${assetId}`);
}

interface ServicePoolLink { ServiceId: number; SoftwarePoolId: number }
/** Link a licence pool to a service (writes oscar.ServiceSoftwarePool). Used from both the Services page and the pool detail. */
export function linkServicePool(serviceId: number, softwarePoolId: number): Promise<ServicePoolLink> {
  return apiClient.post<ServicePoolLink>(`/api/cmdb/services/${serviceId}/software`, { SoftwarePoolId: softwarePoolId });
}
/** Unlink a licence pool from a service (soft-delete the link). */
export function unlinkServicePool(serviceId: number, softwarePoolId: number): Promise<ServicePoolLink> {
  return apiClient.delete<ServicePoolLink>(`/api/cmdb/services/${serviceId}/software/${softwarePoolId}`);
}

// ── Service environments (ADR-002) ───────────────────────────────────────────
/** Environments of a service (for the asset Services-tab environment picker). */
export function getServiceEnvironments(serviceId: number, signal?: AbortSignal): Promise<ServiceEnvironment[]> {
  return apiClient.get<ServiceEnvironment[]>(`/api/cmdb/services/${serviceId}/environments`, signal);
}
export function createServiceEnvironment(serviceId: number, body: ServiceEnvironmentWrite): Promise<ServiceEnvironment> {
  return apiClient.post<ServiceEnvironment>(`/api/cmdb/services/${serviceId}/environments`, body);
}
export function updateServiceEnvironment(serviceId: number, envId: number, body: ServiceEnvironmentWrite): Promise<ServiceEnvironment> {
  return apiClient.patch<ServiceEnvironment>(`/api/cmdb/services/${serviceId}/environments/${envId}`, body);
}
export function deleteServiceEnvironment(serviceId: number, envId: number): Promise<{ EnvironmentId: number }> {
  return apiClient.delete<{ EnvironmentId: number }>(`/api/cmdb/services/${serviceId}/environments/${envId}`);
}
